import notificationIcon from '~images/logo/favicon.png'

/**
 * Browser-native desktop (OS bubble) notifications for the dashboard.
 *
 * Design notes:
 * - The on/off toggle is stored in localStorage (per browser/device), NOT on
 *   the user record. OS notification permission is inherently per-browser, so
 *   a server-side flag would lie on every other device.
 * - Per-type gating (inbox vs team chat vs pending actions) reuses the
 *   existing `enabled_notification_sounds` preference at each call site, so one
 *   set of toggles controls both dings and bubbles. Direct mentions bypass the
 *   toggles (same as their sound) because they are personal and high-signal.
 * - Every bubble click focuses the dashboard tab and dismisses the bubble.
 * - Callers pass a stable `tag` per notification family so a burst of events
 *   replaces the previous bubble instead of stacking ten of them.
 * - This only fires while the dashboard is open in the browser. True push
 *   (browser closed) needs a service worker + server fan-out - the unused
 *   `desktop_notification_token` column on User is reserved for that.
 */

const STORAGE_KEY = 'chckvet:desktop-notifications-enabled'

export const DESKTOP_NOTIFICATION_TAGS = {
    inbox: 'chckvet-inbox',
    teamChat: 'chckvet-team-chat',
    pendingAction: 'chckvet-pending-action',
    mention: 'chckvet-mention',
} as const

export type DesktopNotificationTag =
    (typeof DESKTOP_NOTIFICATION_TAGS)[keyof typeof DESKTOP_NOTIFICATION_TAGS]

type ShowDesktopNotificationOptions = {
    /** Secondary line under the title. Omit for title-only bubbles. */
    body?: string
    /** Stable per-family tag so bursts replace instead of stack. */
    tag?: DesktopNotificationTag
}

/**
 * Shows an OS-level bubble if (and only if) the browser supports it, the user
 * granted OS permission, and they flipped the dashboard toggle on. Never
 * throws - a bubble must never break the feature that triggered it.
 */
export function showDesktopNotification(
    title: string,
    options?: ShowDesktopNotificationOptions,
): void {
    if (!isDesktopNotificationsSupported()) return
    if (!isDesktopNotificationsEnabled()) return
    if (getDesktopNotificationPermission() !== 'granted') return

    try {
        const notification = new Notification(title, {
            body: options?.body,
            tag: options?.tag,
            icon: notificationIcon,
        })
        notification.onclick = () => {
            window.focus()
            notification.close()
        }
    } catch {
        // Permission can be revoked between the check above and construction.
        // Swallow - the underlying event (message, action item) already landed.
    }
}

export function isDesktopNotificationsSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window
}

export function getDesktopNotificationPermission():
    | NotificationPermission
    | 'unsupported' {
    if (!isDesktopNotificationsSupported()) return 'unsupported'
    return Notification.permission
}

export async function requestDesktopNotificationPermission(): Promise<
    NotificationPermission | 'unsupported'
> {
    if (!isDesktopNotificationsSupported()) return 'unsupported'
    return Notification.requestPermission()
}

export function isDesktopNotificationsEnabled(): boolean {
    if (typeof window === 'undefined') return false
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
        return false
    }
}

export function setDesktopNotificationsEnabled(enabled: boolean): void {
    if (typeof window === 'undefined') return
    try {
        window.localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false')
    } catch {
        // Private browsing etc. - the toggle just won't persist.
    }
}
